import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import {
  type FunctionalRequirementRowData,
  normalizeMarkdownContent,
  parseFunctionalRequirementsMarkdown,
} from "~/features/projectTop/pageView/utils/markdownParser";
import { DocumentApi } from "~/functions/api/document";
import { ProcessLockApi } from "~/functions/api/processLock";
import type { RootState } from "~/store";

type RowData = FunctionalRequirementRowData;

interface FunctionalRequirementState {
  data: RowData[];
  copyData: RowData[];
  isEditable: boolean;
  isLoading: boolean;
  error: string | null;
  documentInfo: {
    createdAt: string;
    modifiedAt: string;
    modifiedBy?: {
      display_name: string;
    };
  } | null;
  isMarkdown: boolean;
  markdownContent: string;
  rawContent: string;
  initialMarkdownContent: string;
  sources: { type: string; id: string }[];
}

const initialState: FunctionalRequirementState = {
  data: [],
  copyData: [],
  isEditable: false,
  isLoading: true,
  error: null,
  documentInfo: null,
  isMarkdown: false,
  markdownContent: "",
  rawContent: "",
  initialMarkdownContent: "",
  sources: [],
};

// APIインスタンスの作成
const documentApi = new DocumentApi();
const processLockApi = new ProcessLockApi();

// 機能要件データの取得
export const fetchFunctionalRequirement = createAsyncThunk(
  "functionalRequirement/fetch",
  async (documentIdArg: string | undefined, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;

      // URLパラメータから取得したIDを優先、なければReduxストアから取得
      const documentId = documentIdArg || currentProject.outputs?.["functional-requirements"]?.id;

      if (!documentId) {
        return rejectWithValue("機能要件書が見つかりません");
      }

      const response = await documentApi.get(
        currentUser.organization_id,
        currentProject.id,
        documentId,
      );

      if (response === null) {
        return rejectWithValue("データの取得に失敗しました");
      }

      const rawFunctionalRequirement = response.content ?? "";
      let functionalRequirementRows: RowData[] = [];
      let isMarkdown = false;
      let markdownContent = "";

      try {
        const parsedFunctionalRequirement: { rows?: unknown[] } =
          JSON.parse(rawFunctionalRequirement);
        if (Array.isArray(parsedFunctionalRequirement?.rows)) {
          // 保存データ: [fid, 要求機能, 必要機能, 誰が, どこで, 何をする, 実装方法, 備考]
          // 表示マッピング: 要求機能→業務, 必要機能→機能, 誰が→利用ユーザー, 実装方法→実装方法, 何をする→備考
          functionalRequirementRows = parsedFunctionalRequirement.rows.map((row: unknown) => {
            const r = row as (string | number)[];
            return {
              fid: r[0] ?? "",
              業務: String(r[1] ?? ""), // 要求機能
              機能: String(r[2] ?? ""), // 必要機能
              利用ユーザー: String(r[3] ?? ""), // 誰が
              実装方法: String(r[6] ?? ""), // 実装方法
              備考: String(r[5] ?? ""), // 何をする
            };
          });
        } else {
          // JSONだがrows形式でない場合、マークダウンとしてパースを試みる
          const markdownRows = parseFunctionalRequirementsMarkdown(rawFunctionalRequirement);
          if (markdownRows.length > 0) {
            functionalRequirementRows = markdownRows;
            isMarkdown = false;
          } else {
            isMarkdown = true;
            markdownContent = normalizeMarkdownContent(rawFunctionalRequirement);
          }
        }
      } catch (_error) {
        const markdownRows = parseFunctionalRequirementsMarkdown(rawFunctionalRequirement);
        if (markdownRows.length > 0) {
          functionalRequirementRows = markdownRows;
          isMarkdown = false;
        } else {
          isMarkdown = true;
          markdownContent = normalizeMarkdownContent(rawFunctionalRequirement);
        }
      }

      return {
        data: functionalRequirementRows,
        documentInfo: {
          createdAt: response.created_at || "",
          modifiedAt: response.modified_at || "",
          modifiedBy: response.modified_by,
        },
        isMarkdown,
        markdownContent,
        rawContent: rawFunctionalRequirement,
        sources: response.sources ?? [],
      };
    } catch (_error) {
      return rejectWithValue("データの取得中にエラーが発生しました");
    }
  },
);

// ロックの取得
export const acquireLock = createAsyncThunk(
  "functionalRequirement/acquireLock",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;

      if (!currentProject.outputs?.["functional-requirements"]?.id) {
        return rejectWithValue("機能要件書が見つかりません");
      }

      const response = await processLockApi.lock(currentUser.organization_id, currentProject.id, {
        type: "functional-requirements",
        mode: "edit",
      });

      if (response === null) {
        return rejectWithValue("編集権限を取得できませんでした");
      }

      return true;
    } catch (_error) {
      return rejectWithValue("ロックの取得中にエラーが発生しました");
    }
  },
);

// ロックの解放
export const releaseLock = createAsyncThunk(
  "functionalRequirement/releaseLock",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;

      if (!currentProject.outputs?.["functional-requirements"]?.id) {
        return rejectWithValue("機能要件書が見つかりません");
      }

      await processLockApi.unlock(currentUser.organization_id, currentProject.id, {
        type: "functional-requirements",
        mode: "edit",
      });

      return true;
    } catch (_error) {
      return rejectWithValue("ロックの解放中にエラーが発生しました");
    }
  },
);

// データの更新
export const updateFunctionalRequirement = createAsyncThunk(
  "functionalRequirement/update",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentProject, currentUser } = state.auth;
      const { data, isMarkdown, markdownContent } = state.functionalRequirement;

      if (!currentProject.outputs?.["functional-requirements"]?.id) {
        return rejectWithValue("機能要件書が見つかりません");
      }

      if (isMarkdown) {
        const response = await documentApi.updateContent(
          currentUser.organization_id,
          currentProject.id,
          currentProject.outputs?.["functional-requirements"]?.id,
          { content: markdownContent },
        );

        if (response === null) {
          return rejectWithValue("データの更新に失敗しました");
        }

        await dispatch(releaseLock());

        return true;
      }

      const emptyFieldsFound = data.some((row) => {
        return Object.entries(row).some(([key, value]) => {
          if (key === "fid" || key === "業務" || key === "備考") return false;
          return value === "" || value === undefined || value === null;
        });
      });

      if (emptyFieldsFound) {
        return rejectWithValue("備考以外のカラムを入力してください");
      }

      // 表示名→保存名に逆マッピングして保存
      const headers = [
        "fid",
        "要求機能",
        "必要機能",
        "誰が",
        "どこで",
        "何をする",
        "実装方法",
        "備考",
      ];
      const rows = data.map((row) => [
        row.fid || "",
        row.業務 || "", // 業務 → 要求機能
        row.機能 || "", // 機能 → 必要機能
        row.利用ユーザー || "", // 利用ユーザー → 誰が
        "", // どこで（未使用）
        row.備考 || "", // 備考 → 何をする
        row.実装方法 || "", // 実装方法 → 実装方法
        "", // 備考（旧、未使用）
      ]);

      const updateData = {
        content: JSON.stringify({
          headers,
          rows,
        }),
      };

      const response = await documentApi.updateContent(
        currentUser.organization_id,
        currentProject.id,
        currentProject.outputs?.["functional-requirements"]?.id,
        updateData,
      );

      if (response === null) {
        return rejectWithValue("データの更新に失敗しました");
      }

      // データの更新が成功したら、ロックを解除
      await dispatch(releaseLock());

      return true;
    } catch (_error) {
      return rejectWithValue("データの更新中にエラーが発生しました");
    }
  },
);

const functionalRequirementSlice = createSlice({
  name: "functionalRequirement",
  initialState,
  reducers: {
    setData: (state, action: PayloadAction<RowData[]>) => {
      state.data = action.payload;
      state.isMarkdown = false;
      state.markdownContent = "";
      state.initialMarkdownContent = "";
    },
    setCopyData: (state, action: PayloadAction<RowData[]>) => {
      state.copyData = action.payload;
      state.initialMarkdownContent = state.markdownContent;
    },
    restoreFromCopyData: (state) => {
      if (state.copyData !== undefined) {
        state.data = JSON.parse(JSON.stringify(state.copyData));
      }
      state.markdownContent = state.initialMarkdownContent;
    },
    updateRow: (state, action: PayloadAction<{ index: number; data: RowData }>) => {
      state.data[action.payload.index] = action.payload.data;
    },
    addRow: (state, action: PayloadAction<RowData>) => {
      state.data.push(action.payload);
    },
    deleteRow: (state, action: PayloadAction<number>) => {
      state.data = state.data.filter((_, index) => index !== action.payload);
    },
    clearError: (state) => {
      state.error = null;
    },
    updateMarkdownContent: (state, action: PayloadAction<string>) => {
      state.markdownContent = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchFunctionalRequirement
      .addCase(fetchFunctionalRequirement.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFunctionalRequirement.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isMarkdown = action.payload.isMarkdown;
        state.markdownContent = action.payload.markdownContent;
        state.rawContent = action.payload.rawContent;
        state.initialMarkdownContent = action.payload.markdownContent;
        state.sources = action.payload.sources;
        if (action.payload.isMarkdown) {
          state.data = [];
          state.copyData = [];
        } else {
          state.data = action.payload.data;
          state.copyData = JSON.parse(JSON.stringify(action.payload.data));
        }
        if (action.payload.documentInfo.modifiedBy) {
          state.documentInfo = {
            createdAt: action.payload.documentInfo.createdAt,
            modifiedAt: action.payload.documentInfo.modifiedAt,
            modifiedBy: {
              display_name: action.payload.documentInfo.modifiedBy.display_name,
            },
          };
        } else {
          state.documentInfo = {
            createdAt: action.payload.documentInfo.createdAt,
            modifiedAt: action.payload.documentInfo.modifiedAt,
          };
        }
      })
      .addCase(fetchFunctionalRequirement.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // acquireLock
      .addCase(acquireLock.fulfilled, (state) => {
        state.isEditable = true;
        state.copyData = JSON.parse(JSON.stringify(state.data));
        state.initialMarkdownContent = state.markdownContent;
      })
      .addCase(acquireLock.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // releaseLock
      .addCase(releaseLock.fulfilled, (state) => {
        state.isEditable = false;
      })
      .addCase(releaseLock.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // updateFunctionalRequirement
      .addCase(updateFunctionalRequirement.fulfilled, (state) => {
        state.isEditable = false;
        if (state.isMarkdown) {
          state.initialMarkdownContent = state.markdownContent;
        } else {
          state.copyData = JSON.parse(JSON.stringify(state.data));
        }
      })
      .addCase(updateFunctionalRequirement.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  setData,
  setCopyData,
  restoreFromCopyData,
  updateRow,
  addRow,
  deleteRow,
  clearError,
  updateMarkdownContent,
} = functionalRequirementSlice.actions;

export default functionalRequirementSlice.reducer;
